/**
 * @fileOverview See {@link ./README.md}'s "aggregate-tracker.ts" section for details.
 */

/* eslint-disable i18next/no-literal-string */
import { ContextSetter, createFanOut, PayloadHandlers } from '../helpers/fan-out';
import { Events, AnalyticsContext } from '../events';
import { createConsoleLogTracker } from './console-log-tracker';
import { createInternalGTMTracker } from './internal-gtm-tracker';
import { createSiftTracker } from './sift-tracker';
import { createSegmentTracker } from './segment-tracker';

/**
 * Add configuration details for trackers here.
 */
export type AggregateTrackerConfig = {
  /* FLOW(add-a-tracker).STEP_3a: add your tracker's configuration here */
  internalGTMEnabled: boolean;
  segmentKey: string;
  siftBeaconKey: string;
  siftEnabled: boolean;
};

// prettier-ignore
/**
 * Creates an object that looks like:
 * {
 *   addedProductToCart: (payload: Events['addedProductToCart'], context: AnalyticsContext) => void;
 *   placedOrder: (payload: Events['placedOrder'], context: AnalyticsContext) => void;
 *   ...
 *
 *   // to set the context passed to all event handlers
 *   setContext: (context: AnalyticsContext) => void
 * }
 */
export const createAggregateTracker = (
  config?: AggregateTrackerConfig
): ContextSetter<AnalyticsContext> & PayloadHandlers<Events, void> =>
  createFanOut<Events, AnalyticsContext>((fanOut) => ({
    /**
     * Wondering what `fanOut` does? It merely converts
     *   fanOut('foo')
     * to
     *   (payload: Events['foo']) => trackers.forEach((t) => t.foo(payload, context))
     */

    /*
     * FLOW(track-an-event).STEP_2: add your event here (and [STEP_3] in every other *-tracker.ts file in this folder)
     */
    addedProductToCart: fanOut('addedProductToCart'),
    checkoutStarted: fanOut('checkoutStarted'),
    dutchiePayConnectBankStep: fanOut('dutchiePayConnectBankStep'),
    dutchiePayEnrollmentButtonClicked: fanOut('dutchiePayEnrollmentButtonClicked'),
    dutchiePayLearnMoreButtonClicked: fanOut('dutchiePayLearnMoreButtonClicked'),
    dutchiePayCTAButtonClicked: fanOut('dutchiePayCTAButtonClicked'),
    dutchiePayEnrollmentStep: fanOut('dutchiePayEnrollmentStep'),
    placedOrder: fanOut('placedOrder'),
    productClicked: fanOut('productClicked'),
    removedProductFromCart: fanOut('removedProductFromCart'),
    searchedForProduct: fanOut('searchedForProduct'),
    searchResults: fanOut('searchResults'),
    viewedCheckout: fanOut('viewedCheckout'),
    viewedProduct: fanOut('viewedProduct'),
    cartViewed: fanOut('cartViewed'),
  }), {
    activeNavigationBreadcrumbs: null,
    activeMenuVariant: null,
    activeProductPosition: null,
    activeCart: null,
    activeDispensary: null,
    activeOrder: null,
    activePath: null,
    activeUser: null,
    activeIsUserEnrolledDutchiePay: null,
    checkoutToken: null,
  }, config ? [
    /* FLOW(add-a-tracker).STEP_3b: create your tracker here */
    createConsoleLogTracker(),
    createSiftTracker({ beaconKey: config.siftBeaconKey, enabled: config.siftEnabled }),
    createSegmentTracker(),
    createInternalGTMTracker({ enabled: config.internalGTMEnabled }),
  ] : []);
